/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';

type RRowGap = 'sm' | 'md' | 'lg';
type RRowAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';
type RRowJustify = 'start' | 'center' | 'end' | 'between' | 'around';
type RRowComponent = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'nav';

interface RRowProps {
    children: React.ReactNode;
    /** Gap between children: `sm` (8px), `md` (16px, default), or `lg` (24px). */
    gap?: RRowGap;
    /** Cross-axis alignment (vertical). Defaults to `center`. */
    align?: RRowAlign;
    /** Main-axis alignment (horizontal). Defaults to `start`. */
    justify?: RRowJustify;
    /** If true, allows children to wrap onto multiple lines. */
    wrap?: boolean;
    /** If true, stretches to 100% of parent width. */
    fullWidth?: boolean;
    /** HTML element to render. Defaults to `div`. */
    component?: RRowComponent;
    className?: string;
}

const GAP_PX: Record<RRowGap, number> = { sm: 8, md: 16, lg: 24 };
const ALIGN_VALUE: Record<RRowAlign, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    baseline: 'baseline',
    stretch: 'stretch',
};
const JUSTIFY_VALUE: Record<RRowJustify, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
    around: 'space-around',
};

type RowStyleProps = {
    $gap: RRowGap;
    $align: RRowAlign;
    $justify: RRowJustify;
    $wrap: boolean;
    $fullWidth: boolean;
};

const rowStyles = ({ $gap, $align, $justify, $wrap, $fullWidth }: RowStyleProps) => ({
    display: 'flex',
    flexDirection: 'row' as const,
    gap: GAP_PX[$gap],
    alignItems: ALIGN_VALUE[$align],
    justifyContent: JUSTIFY_VALUE[$justify],
    flexWrap: ($wrap ? 'wrap' : 'nowrap') as 'wrap' | 'nowrap',
    width: $fullWidth ? '100%' : undefined,
});

const shouldForwardRowProp = (prop: string) => !['$gap', '$align', '$justify', '$wrap', '$fullWidth'].includes(prop);

const DivRow = styled('div', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(rowStyles);
const SectionRow = styled('section', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const ArticleRow = styled('article', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const AsideRow = styled('aside', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const HeaderRow = styled('header', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const FooterRow = styled('footer', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const NavRow = styled('nav', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(rowStyles);

const COMPONENTS = {
    div: DivRow,
    section: SectionRow,
    article: ArticleRow,
    aside: AsideRow,
    header: HeaderRow,
    footer: FooterRow,
    nav: NavRow,
};

export function RRow({
    children,
    gap = 'md',
    align = 'center',
    justify = 'start',
    wrap = false,
    fullWidth = false,
    component = 'div',
    className,
}: RRowProps) {
    const Component = COMPONENTS[component];
    return (
        <Component
            $gap={gap}
            $align={align}
            $justify={justify}
            $wrap={wrap}
            $fullWidth={fullWidth}
            className={className}
        >
            {children}
        </Component>
    );
}
/* eslint-enable no-comments/disallowComments */
